import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiFetch, setCsrfToken } from './api';
import { ApiError, type Capability, type MeResponse, type Role } from '../types/api';

interface AuthState {
  user: MeResponse | null;
  loading: boolean;
  error: string | null;
}

/**
 * What `login()` resolved to: `'totp_required'` — the password was right,
 * a second factor is pending; `'signed_in'` — the server returned a full
 * session straight away (no TOTP step), `user` is already set. Review fix
 * 25/9: both paths used to return `'totp_required'`.
 */
export type LoginResult = 'totp_required' | 'signed_in';

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyTotp: (code: string, kind?: 'code' | 'recovery') => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<MeResponse | null>;
  isAdmin: boolean;
  isEditor: boolean;
  hasRole: (role: Role) => boolean;
  /**
   * False when the member's custom role takes `capability` away (K8). Only
   * for hiding buttons: the server enforces it either way.
   */
  can: (capability: Capability) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ROLE_RANK: Record<Role, number> = { viewer: 0, editor: 1, admin: 2 };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, loading: true, error: null });

  // The `/auth/me` call on mount can answer after the provider is gone (a
  // test ending, a remount). Setting state then makes React read `window`,
  // which a torn-down test environment no longer has (CI on main, 6/10).
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const me = await apiFetch<MeResponse>('/auth/me');
      setCsrfToken(me.csrfToken);
      if (mounted.current) setState({ user: me, loading: false, error: null });
      return me;
    } catch (err) {
      // Only a 401 means the session is gone. Since the step-up window
      // (21/10-2026) this also runs after every step-up action, so a brief
      // network or server error must not sign the user out of the UI.
      if (mounted.current) {
        setState((s) =>
          err instanceof ApiError && err.status === 401
            ? { user: null, loading: false, error: null }
            : { ...s, loading: false },
        );
      }
      return null;
    }
  }, []);

  useEffect(() => {
    void refreshMe();
  }, [refreshMe]);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    setState((s) => ({ ...s, error: null }));
    const result = await apiFetch<{ status: 'totp_required' } | MeResponse>('/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    if ('status' in result && result.status === 'totp_required') {
      return 'totp_required';
    }
    setCsrfToken((result as MeResponse).csrfToken);
    setState({ user: result as MeResponse, loading: false, error: null });
    return 'signed_in';
  }, []);

  const verifyTotp = useCallback(async (code: string, kind: 'code' | 'recovery' = 'code') => {
    const me = await apiFetch<MeResponse>('/auth/totp', {
      method: 'POST',
      body: kind === 'recovery' ? { recoveryCode: code } : { code },
    });
    setCsrfToken(me.csrfToken);
    setState({ user: me, loading: false, error: null });
  }, []);

  const logout = useCallback(async () => {
    await apiFetch('/auth/logout', { method: 'POST' }).catch(() => undefined);
    setCsrfToken(null);
    setState({ user: null, loading: false, error: null });
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const role = state.user?.role ?? null;
    const denied = state.user?.deniedCapabilities ?? [];
    return {
      ...state,
      login,
      verifyTotp,
      logout,
      refreshMe,
      isAdmin: role === 'admin',
      isEditor: role === 'admin' || role === 'editor',
      hasRole: (required: Role) => (role ? ROLE_RANK[role] >= ROLE_RANK[required] : false),
      can: (capability: Capability) => !denied.includes(capability),
    };
  }, [state, login, verifyTotp, logout, refreshMe]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
